/**
 * Portfolio page configuration
 *
 * Sections can be managed from:
 * 1. Database (via /admin/portfolio) - preferred, UNLIMITED sections and images
 * 2. Environment variables (fallback) - limited to 3 sections
 *
 * Admin Panel (/admin/portfolio):
 * - Unlimited sections
 * - Unlimited images per section
 * - Toggle "portfolio público" (site_settings.portfolio_public_enabled)
 *
 * Env vars (fallback only if DB not available):
 * - VITE_PORTFOLIO_SECTION_TITLES: comma-separated titles (max 3), e.g. "Lookbook 1,Lookbook 2,Lookbook 3"
 * - VITE_PORTFOLIO_SECTION_1_URLS: comma-separated image URLs for section 1
 * - VITE_PORTFOLIO_SECTION_2_URLS: comma-separated image URLs for section 2
 * - VITE_PORTFOLIO_SECTION_3_URLS: comma-separated image URLs for section 3
 */

const MAX_SECTIONS = 3; // Only for env vars fallback

export interface PortfolioSection {
  id?: string;
  title: string;
  urls: string[];
}

/** Entrada de imagen con soporte para formato horizontal (wide) */
export interface PortfolioImageEntry {
  url: string;
  wide: boolean;
}

export type PortfolioLoadResult = {
  sections: PortfolioSection[];
  /** false = admin desactivó el portfolio público (no usar imágenes de BD en / ni /portfolio) */
  portfolioPublicEnabled: boolean;
};

const WIDE_PREFIX = "wide:";

/** Parsea URL: "wide:https://..." → { url, wide: true }; "https://..." → { url, wide: false } */
export function parsePortfolioUrl(raw: string): PortfolioImageEntry {
  if (raw.startsWith(WIDE_PREFIX)) {
    return { url: raw.slice(WIDE_PREFIX.length), wide: true };
  }
  return { url: raw, wide: false };
}

/** Serializa para guardar en DB */
export function serializePortfolioUrl(url: string, wide: boolean): string {
  return wide ? WIDE_PREFIX + url : url;
}

let cachedResult: PortfolioLoadResult | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 60 * 1000; // 1 minute - so admin saves appear quickly

/** Llamar tras guardar en /admin/portfolio para que la misma sesión no siga mostrando datos viejos. */
export function invalidatePortfolioSectionsCache(): void {
  cachedResult = null;
  cacheTimestamp = 0;
}

/** Mock data when env vars are not set (development / preview). */
const MOCK_SECTIONS: PortfolioSection[] = [
  {
    title: "Editorial Verano",
    urls: [
      "https://picsum.photos/seed/p1/800/1000",
      "https://picsum.photos/seed/p2/800/600",
      "https://picsum.photos/seed/p3/600/800",
      "https://picsum.photos/seed/p4/800/1000",
      "https://picsum.photos/seed/p5/800/600",
    ],
  },
  {
    title: "Lookbook Otoño",
    urls: [
      "https://picsum.photos/seed/p6/800/1000",
      "https://picsum.photos/seed/p7/600/800",
      "https://picsum.photos/seed/p8/800/600",
      "https://picsum.photos/seed/p9/800/1000",
    ],
  },
  {
    title: "Campaña 2024",
    urls: [
      "https://picsum.photos/seed/p10/800/600",
      "https://picsum.photos/seed/p11/800/1000",
      "https://picsum.photos/seed/p12/600/800",
    ],
  },
];

function parseCommaList(value: string | undefined): string[] {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function isValidUrl(s: string): boolean {
  return s.startsWith("http://") || s.startsWith("https://");
}

function parseUrlList(value: string | undefined): string[] {
  return parseCommaList(value).filter(isValidUrl);
}

/** Section titles from env (max 3; names of each fashion production / lookbook). */
export const sectionTitles: string[] = parseCommaList(
  import.meta.env.VITE_PORTFOLIO_SECTION_TITLES as string | undefined,
).slice(0, MAX_SECTIONS);

const sectionUrlKeys = [
  import.meta.env.VITE_PORTFOLIO_SECTION_1_URLS as string | undefined,
  import.meta.env.VITE_PORTFOLIO_SECTION_2_URLS as string | undefined,
  import.meta.env.VITE_PORTFOLIO_SECTION_3_URLS as string | undefined,
];

/**
 * Load sections from database (respeta portfolioPublicEnabled)
 */
async function loadSectionsFromDB(): Promise<PortfolioLoadResult | null> {
  try {
    const response = await fetch("/api/portfolio", { cache: "no-store" });
    if (!response.ok) return null;

    const data = await response.json();
    const portfolioPublicEnabled = data.portfolioPublicEnabled !== false;
    const raw = data.sections || [];

    if (!portfolioPublicEnabled) {
      return { sections: [], portfolioPublicEnabled: false };
    }

    return { sections: raw as PortfolioSection[], portfolioPublicEnabled: true };
  } catch (error) {
    console.error("[portfolioConfig] Error loading from DB:", error);
    return null;
  }
}

/**
 * Get sections from environment variables (fallback)
 */
function getSectionsFromEnv(): PortfolioSection[] {
  const sections: PortfolioSection[] = [];

  for (let i = 0; i < MAX_SECTIONS; i++) {
    const title = sectionTitles[i];
    if (!title) continue;

    const urls = parseUrlList(sectionUrlKeys[i]);
    sections.push({ title, urls });
  }

  if (sections.length === 0) {
    return MOCK_SECTIONS;
  }

  return sections;
}

/**
 * Builds portfolio sections with the following priority:
 * 1. Database (via /api/portfolio) - cached ~1 min
 * 2. Environment variables (fallback)
 * 3. Mock data (development)
 *
 * This function is synchronous for backwards compatibility.
 * Use getPortfolioSectionsAsync() for async loading.
 */
export function getPortfolioSections(): PortfolioSection[] {
  if (cachedResult && Date.now() - cacheTimestamp < CACHE_TTL) {
    if (!cachedResult.portfolioPublicEnabled) return [];
    return cachedResult.sections;
  }

  return getSectionsFromEnv();
}

/**
 * Async version that loads from DB first, then falls back to env vars
 * @param forceRefresh - if true, bypass cache and always fetch (use when visiting /portfolio so admin saves appear immediately)
 */
export async function getPortfolioSectionsAsync(
  forceRefresh = false,
): Promise<PortfolioLoadResult> {
  if (!forceRefresh && cachedResult && Date.now() - cacheTimestamp < CACHE_TTL) {
    return cachedResult;
  }

  const db = await loadSectionsFromDB();

  if (db) {
    if (!db.portfolioPublicEnabled) {
      cachedResult = { sections: [], portfolioPublicEnabled: false };
      cacheTimestamp = Date.now();
      return cachedResult;
    }

    if (db.sections.length > 0) {
      const normalized = db.sections.map(
        (s: { id?: string; title?: string; urls?: unknown }) => ({
          id: s.id,
          title: s.title ?? "Sección",
          urls: Array.isArray(s.urls)
            ? s.urls.filter((u): u is string => typeof u === "string")
            : [],
        }),
      );
      cachedResult = { sections: normalized, portfolioPublicEnabled: true };
      cacheTimestamp = Date.now();
      return cachedResult;
    }
  }

  const envSections = getSectionsFromEnv();
  cachedResult = { sections: envSections, portfolioPublicEnabled: true };
  cacheTimestamp = Date.now();
  return cachedResult;
}
